import { ImplAPI, Req } from './ImplAPI';

// ============================================================================
// GraphQL Queries
// ============================================================================

export const GET_ALL_PRODUCTS_QUERY = `
  query getAllProducts {
    products(first: 250) {
      edges {
        node {
          id
          title
          handle
          descriptionHtml
          productType
          tags
          collections(first: 5) {
            edges {
              node {
                id
                title
                handle
              }
            }
          }
          featuredImage {
            url
            altText
          }
          allergens: metafield(namespace: "shopify", key: "allergen-information") {
            references(first: 10) {
              nodes {
                ... on Metaobject {
                  field(key: "label") {
                    value
                  } 
                }
              }
            }
          }
          nutrition_facts: metafield(namespace: "custom", key: "nutrition_facts") {
            reference {
              ... on Metaobject {
                calories: field(key: "calories") { value }
                totalFat: field(key: "total_fat") { value }
                saturatedFat: field(key: "saturated_fat") { value }
                transFat: field(key: "trans_fat") { value }
                cholesterol: field(key: "cholesterol") { value }
                totalCarbohydrates: field(key: "total_carbohydrates") { value }
                dietaryFiber: field(key: "dietary_fiber") { value }
                totalSugars: field(key: "total_sugars") { value }
                protein: field(key: "protein") { value }
                servingSize: field(key: "serving_size") { 
                  reference {
                    ... on Metaobject {
                      amount: field(key: "amount") { value }
                      weight: field(key: "weight") { value }
                    }
                  }
                }
              }
            }
          }
          savings: metafield(namespace: "custom", key: "savings") {
            value
          }
          # We fetch the first 3 variants specifically
          variants(first: 3) {
            edges {
              node {
                id
                title
                sku
                availableForSale
                price {
                  amount
                  currencyCode
                }
                compareAtPrice {
                  amount
                  currencyCode
                }
                image {
                  url
                  altText
                }
                selectedOptions {
                  name
                  value
                }
              }
            }
          }
        }
      }
    }
  }
`;
// ============================================================================
// Types & Interfaces
// ============================================================================

// Generic GraphQL payload structures (You can also export/share these from CartAPI)
export interface GraphQLBody<TVariables = Record<string, never>> {
  query: string;
  variables?: TVariables;
}

export interface GraphQLResponse<TData> {
  data: TData;
  errors?: Array<{ message: string }>;
}

// Specific Storefront API Return Types
export interface ShopifyCollectionNode {
  id: string;
  title: string;
  handle: string;
}

export interface ShopifyProductNode {
  id: string;
  title: string;
  handle: string;
  productType: string;
  tags: string[];
  collections: {
    edges: Array<{ node: ShopifyCollectionNode }>;
  };
  featuredImage: {
    url: string;
    altText: string | null;
  } | null;
  priceRange: {
    minVariantPrice: {
      amount: string;
      currencyCode: string;
    };
  };
  variants: {
    edges: Array<{ node: { id: string } }>;
  };
}

export interface GetAllProductsData {
  products: {
    edges: Array<{ node: ShopifyProductNode }>;
  };
}

// ============================================================================
// API Class Implementation
// ============================================================================
const STOREFRONT_API = import.meta.env.VITE_SHOPIFY_STORE_DOMAIN;
export class ProductAPI extends ImplAPI {
  // Points to your backend proxy or directly to /api/2024-04/graphql.json
  static get route() {
    return '/api/2024-04/graphql.json';
  }

  /**
   * QUERY AGNOSTIC RUNNER
   * Handles the generic structure of a GraphQL query.
   * Note: Even though it's a "Query", GraphQL still requires a POST request.
   */
  static async post<GetAllProductsData, TVariables = undefined>() {
    return Req<GraphQLResponse<GetAllProductsData>, GraphQLBody<TVariables>>({
      method: 'POST',
      route: this.route,
      body: { query: GET_ALL_PRODUCTS_QUERY },
      uri: STOREFRONT_API,
      headers: {
        'X-Shopify-Storefront-Access-Token': import.meta.env
          .VITE_SHOPIFY_STOREFRONT_PUBLIC_TOKEN,
      },
    });
  }
}
